import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { DsToastService } from '@bmw-ds/components';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  catchError,
  filter as rxFilter,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { loadDocumentTypes } from '../../shared/upload-files/state/upload-files.actions';
import { selectLoading } from '../../state/current-user/current-user.selectors';
import { ApiError } from '../../common/http/api-error.types';
import { extractApiError } from '../../common/http/api-error.utils';
import { ClaimActionEnum } from '../models/enums/claim-action.enum';
import { CostTypeEnum } from '../models/enums/cost-type.enum';
import { ICostTypeDetails } from '../models/cost-type.model';
import { RecoveryActionEnum } from '../models/enums/recovery-action.enum';
import { UploadType } from '../models/enums/upload-type.enum';
import { ClaimApiService } from '../services/claim-api.service';
import { ClaimFilesService } from '../services/claim-files.service';
import { ClaimPermissionsService } from '../services/claim-permissions.service';
import { CostTypeApiService } from '../services/cost-type-api.service';
import { RecoveryApiService } from '../services/recovery-api.service';
import { SettlementSummaryApiService } from '../services/settlement-summary-api.service';
import { ClaimWorkflowActions } from './claim-workflow.actions';
import { claimWorkflowFeature } from './claim-workflow.feature';

const toApiError = (error: unknown): ApiError | null =>
  error instanceof HttpErrorResponse ? extractApiError(error) : null;

const reloadClaimWorkflowData = (claimId: number): Action[] => [
  ClaimWorkflowActions.loadClaim({
    claimId,
    filterHistoryByUserComments: true,
  }),
  ClaimWorkflowActions.loadRecoveries({ claimId }),
  ClaimWorkflowActions.loadSettlementSummary({ claimId }),
  ClaimWorkflowActions.loadRecoverySummary({ claimId }),
];

const reloadRecoveryData = (claimId: number): Action[] => [
  ClaimWorkflowActions.loadRecoveries({ claimId }),
  ClaimWorkflowActions.loadRecoveryDamages({ claimId }),
  ClaimWorkflowActions.loadRecoverySummary({ claimId }),
];

const reloadAfterCostTypeChange = (
  claimId: number,
  recoveryId: number | null
): Action[] =>
  recoveryId === null
    ? [ClaimWorkflowActions.loadSettlementSummary({ claimId })]
    : reloadRecoveryData(claimId);

const matchesActiveClaim =
  (store: Store) =>
  <T extends { claimId: number }>(source: Observable<T>): Observable<T> =>
    source.pipe(
      withLatestFrom(store.select(claimWorkflowFeature.selectActiveClaimId)),
      rxFilter(
        ([action, activeClaimId]) =>
          activeClaimId === null || action.claimId === activeClaimId
      ),
      map(([action]) => action)
    );

export const initializeRecoveryTab$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.initializeRecoveryTab),
      mergeMap(({ claimId }) => reloadRecoveryData(claimId))
    ),
  { functional: true }
);

export const initializeSettlementTab$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.initializeSettlementTab),
      mergeMap(({ claimId }) => [
        ClaimWorkflowActions.loadSettlementSummary({ claimId }),
        ClaimWorkflowActions.loadCostTypeOptions({ claimId }),
      ])
    ),
  { functional: true }
);

export const initializeUploadsTab$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.initializeUploadsTab),
      map(() => loadDocumentTypes())
    ),
  { functional: true }
);

export const uploadClaimFiles$ = createEffect(
  (
    actions$ = inject(Actions),
    files = inject(ClaimFilesService),
    toast = inject(DsToastService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.uploadClaimFiles),
      mergeMap(({ claimId, files: selectedFiles, documentType }) => {
        if (selectedFiles.length === 0) {
          return of(ClaimWorkflowActions.uploadClaimFilesSuccess());
        }
        return files
          .uploadFileForClaim(
            claimId,
            selectedFiles,
            documentType,
            UploadType.UPLOAD
          )
          .pipe(
            mergeMap(() => [
              ClaimWorkflowActions.uploadClaimFilesSuccess(),
              ClaimWorkflowActions.loadClaim({
                claimId,
                filterHistoryByUserComments: true,
              }),
            ]),
            catchError((error) => {
              // API errors are toasted by the error interceptor; S3 errors bypass it.
              if (!(error instanceof HttpErrorResponse)) {
                toast.pushToast({
                  id: 'file-upload-error',
                  tone: 'critical',
                  duration: 5000,
                  toastText: translate.instant('vin.costTypes.fileUploadError'),
                });
              }
              return of(
                ClaimWorkflowActions.uploadClaimFilesFailure({
                  error: toApiError(error),
                }),
                ClaimWorkflowActions.loadClaim({
                  claimId,
                  filterHistoryByUserComments: true,
                })
              );
            })
          );
      })
    ),
  { functional: true }
);

export const deleteClaimFiles$ = createEffect(
  (actions$ = inject(Actions), files = inject(ClaimFilesService)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.deleteClaimFiles),
      mergeMap(({ claimId, fileIds }) => {
        if (fileIds.length === 0) {
          return of(ClaimWorkflowActions.deleteClaimFilesSuccess());
        }
        return forkJoin(
          fileIds.map((fileId) => files.deleteFileFromClaim(claimId, fileId))
        ).pipe(
          mergeMap(() => [
            ClaimWorkflowActions.deleteClaimFilesSuccess(),
            ClaimWorkflowActions.loadClaim({
              claimId,
              filterHistoryByUserComments: true,
            }),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.deleteClaimFilesFailure({
                error: toApiError(error),
              }),
              ClaimWorkflowActions.loadClaim({
                claimId,
                filterHistoryByUserComments: true,
              })
            )
          )
        );
      })
    ),
  { functional: true }
);

export const loadClaim$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(ClaimApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadClaim),
      matchesActiveClaim(store),
      switchMap(({ claimId, filterHistoryByUserComments }) =>
        api.getClaimById(claimId, filterHistoryByUserComments).pipe(
          map((claim) => ClaimWorkflowActions.loadClaimSuccess({ claim })),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadClaimFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const loadRecoveries$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(RecoveryApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadRecoveries),
      matchesActiveClaim(store),
      switchMap(({ claimId }) =>
        api.getRecoveriesByClaimId(claimId, true).pipe(
          map((recoveries) =>
            ClaimWorkflowActions.loadRecoveriesSuccess({ recoveries })
          ),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadRecoveriesFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const loadRecoveryDamages$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(RecoveryApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadRecoveryDamages),
      matchesActiveClaim(store),
      switchMap(({ claimId }) =>
        api.getRecoveryDamagesByClaim(claimId).pipe(
          map((recoveryDamages) =>
            ClaimWorkflowActions.loadRecoveryDamagesSuccess({ recoveryDamages })
          ),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadRecoveryDamagesFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const loadSettlementSummary$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(SettlementSummaryApiService),
    permissions = inject(ClaimPermissionsService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadSettlementSummary),
      matchesActiveClaim(store),
      switchMap(({ claimId }) =>
        store.select(selectLoading).pipe(
          rxFilter((loading) => !loading),
          take(1),
          map(() => claimId)
        )
      ),
      switchMap((claimId) => {
        if (!permissions.canReadSettlementSummary()) {
          return of(
            ClaimWorkflowActions.loadSettlementSummarySuccess({
              settlementSummary: [],
            })
          );
        }
        return api.getSettlementSummary(claimId).pipe(
          map((settlementSummary) =>
            ClaimWorkflowActions.loadSettlementSummarySuccess({
              settlementSummary,
            })
          ),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadSettlementSummaryFailure({
                error: toApiError(error),
              })
            )
          )
        );
      })
    ),
  { functional: true }
);

export const loadRecoverySummary$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(RecoveryApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadRecoverySummary),
      matchesActiveClaim(store),
      switchMap(({ claimId }) =>
        api.getRecoverySummary(claimId).pipe(
          map((recoverySummary) =>
            ClaimWorkflowActions.loadRecoverySummarySuccess({ recoverySummary })
          ),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadRecoverySummaryFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const loadCostTypeOptions$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(CostTypeApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.loadCostTypeOptions),
      matchesActiveClaim(store),
      switchMap(({ claimId }) =>
        api.getOptions(claimId).pipe(
          map((options) =>
            ClaimWorkflowActions.loadCostTypeOptionsSuccess({ options })
          ),
          catchError((error) =>
            of(
              ClaimWorkflowActions.loadCostTypeOptionsFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

const CLAIM_ACTION_SUCCESS_TOASTS: Partial<Record<ClaimActionEnum, string>> = {
  [ClaimActionEnum.SURVEYOR_SUBMIT_SURVEY]:
    'claims.toasts.surveyReportSubmittedToRepairShop',
  [ClaimActionEnum.SURVEYOR_REACTS_TO_AMENDMENT]:
    'claims.toasts.amendmentSubmittedToRepairShop',
  [ClaimActionEnum.REJECT_CLAIM]: 'claims.toasts.claimRejectedSuccessfully',
  [ClaimActionEnum.CLOSE_CLAIM]: 'claims.toasts.claimClosedSuccessfully',
  [ClaimActionEnum.CHANGE_MASTER_DATA_BY_SUPERUSER]:
    'claims.toasts.masterDataChanged',
  [ClaimActionEnum.ACCEPT_BY_CHL_SET]:
    'claims.toasts.claimAcceptedSuccessfully',
  [ClaimActionEnum.FORWARD_TO_ANOTHER_CHL_SET]:
    'claims.toasts.claimForwardedToAnotherCHLSuccessfully',
  [ClaimActionEnum.SUBMIT_CLAIM]: 'claims.toasts.claimSubmittedToClaimHandler',
  [ClaimActionEnum.ACCEPT_BY_PODB]: 'claims.toasts.claimAcceptedSuccessfully',
  [ClaimActionEnum.REJECT_BY_PODB]: 'claims.toasts.claimRejected',
  [ClaimActionEnum.ACCEPT_BY_PODF]: 'claims.toasts.claimFinallyAccepted',
  [ClaimActionEnum.REJECT_BY_PODF]: 'claims.toasts.claimRejected',
  [ClaimActionEnum.REQUEST_AMENDMENT]:
    'claims.requestAmendmentModal.requestAmendmentSuccess',
};

const pushClaimActionSuccessToast = (
  toast: DsToastService,
  translate: TranslateService,
  action: ClaimActionEnum,
  params?: Record<string, string>
): void => {
  const toastKey = CLAIM_ACTION_SUCCESS_TOASTS[action];
  if (toastKey === undefined) {
    return;
  }
  toast.pushToast({
    id: 'claim-action-success',
    tone: 'positive',
    duration: 3000,
    toastText: translate.instant(toastKey, params),
  });
};

const REPAIR_COSTS_EXCEEDS_LIMIT_KEY = 'repairCostsExceedsLimit';

const isRepairCostsExceedsLimit = (error: ApiError | null): boolean =>
  error?.violations.some(
    (violation) => violation.details?.key === REPAIR_COSTS_EXCEEDS_LIMIT_KEY
  ) ?? false;

export const performClaimAction$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(ClaimApiService),
    toast = inject(DsToastService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.performClaimAction),
      mergeMap(({ claimId, action, body, successToastParams }) =>
        api.performAction(claimId, action, body).pipe(
          tap(() =>
            pushClaimActionSuccessToast(
              toast,
              translate,
              action,
              successToastParams
            )
          ),
          mergeMap(() => [
            ClaimWorkflowActions.performClaimActionSuccess({ action }),
            ...reloadClaimWorkflowData(claimId),
          ]),
          catchError((error) => {
            const apiError = toApiError(error);
            return of(
              ClaimWorkflowActions.performClaimActionFailure({
                action,
                error: apiError,
              }),
              ...(action === ClaimActionEnum.SUBMIT_CLAIM &&
              isRepairCostsExceedsLimit(apiError)
                ? [ClaimWorkflowActions.markRequestAmendmentAvailable()]
                : [])
            );
          })
        )
      )
    ),
  { functional: true }
);

export const addLiableToRecovery$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(RecoveryApiService),
    toast = inject(DsToastService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.addLiableToRecovery),
      mergeMap(({ request }) =>
        api.addLiableToRecovery(request).pipe(
          tap(() =>
            toast.pushToast({
              id: 'add-liable-success',
              tone: 'positive',
              toastText: translate.instant(
                'claims.toasts.successfulAdditionOfLiable'
              ),
            })
          ),
          mergeMap(() => [
            ClaimWorkflowActions.addLiableToRecoverySuccess(),
            ...reloadRecoveryData(request.claimId),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.addLiableToRecoveryFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const patchRecovery$ = createEffect(
  (actions$ = inject(Actions), api = inject(RecoveryApiService)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.patchRecovery),
      mergeMap(({ claimId, recoveryId, request }) =>
        api.patchRecovery(recoveryId, request).pipe(
          mergeMap(() => [
            ClaimWorkflowActions.patchRecoverySuccess(),
            ...reloadRecoveryData(claimId),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.patchRecoveryFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

const RECOVERY_ACTION_SUCCESS_TOASTS: Partial<
  Record<RecoveryActionEnum, string>
> = {
  [RecoveryActionEnum.FORWARD_TO_ANOTHER_CHL_REC]:
    'claims.toasts.chlRecChangedSuccessfully',
  [RecoveryActionEnum.ACCEPT_BY_CAUSER]:
    'claims.toasts.recoverySuccessfullyAccepted',
  [RecoveryActionEnum.REJECT_BY_CAUSER]: 'claims.toasts.recoveryRejected',
  [RecoveryActionEnum.ROLLBACK_BY_CHL_REC]:
    'claims.toasts.recoverySuccessfullyRolledBack',
  [RecoveryActionEnum.FINALIZE_RECOVERY]: 'claims.toasts.recoveryFinalised',
  [RecoveryActionEnum.FINALIZE_RECOVERY_NOT_POSSIBLE]:
    'claims.toasts.recoveryFinalised',
  [RecoveryActionEnum.REJECT_BY_CHL_REC]: 'claims.toasts.recoveryRejected',
  [RecoveryActionEnum.SUBMIT_TO_CAUSER]:
    'claims.toasts.recoverySuccessfullySubmitted',
  [RecoveryActionEnum.ACCEPT_RECOVERY_BY_SUPERUSER]:
    'claims.toasts.recoveryAcceptedForTheCauser',
  [RecoveryActionEnum.CHANGE_MASTER_DATA_BY_SUPERUSER]:
    'claims.toasts.masterDataChanged',
  [RecoveryActionEnum.REQUEST_TIME_BAR_EXTENSION]:
    'claims.toasts.timeBarExtensionRequested',
  [RecoveryActionEnum.REJECT_TIME_BAR_EXTENSION]:
    'claims.toasts.timeBarExtensionRejected',
  [RecoveryActionEnum.ACCEPT_TIME_BAR_EXTENSION]:
    'claims.toasts.timeBarExtensionAccepted',
};

const RECOVERY_ACTION_TOAST_TONES: Partial<
  Record<RecoveryActionEnum, 'critical'>
> = {
  [RecoveryActionEnum.REJECT_TIME_BAR_EXTENSION]: 'critical',
};

export const performRecoveryAction$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(RecoveryApiService),
    toast = inject(DsToastService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.performRecoveryAction),
      mergeMap(({ claimId, recoveryId, action, body, successToastParams }) =>
        api.performAction(recoveryId, action, body).pipe(
          tap(() => {
            const toastKey = RECOVERY_ACTION_SUCCESS_TOASTS[action];
            if (toastKey !== undefined) {
              toast.pushToast({
                id: 'recovery-action-success',
                tone: RECOVERY_ACTION_TOAST_TONES[action] ?? 'positive',
                toastText: translate.instant(toastKey, successToastParams),
              });
            }
          }),
          mergeMap(() => [
            ClaimWorkflowActions.performRecoveryActionSuccess(),
            ...reloadRecoveryData(claimId),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.performRecoveryActionFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const updateCostType$ = createEffect(
  (actions$ = inject(Actions), api = inject(CostTypeApiService)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.updateCostType),
      mergeMap(({ claimId, recoveryId, costTypeId, request }) =>
        api.updateCostType(costTypeId, request).pipe(
          mergeMap((costType: ICostTypeDetails) => [
            ClaimWorkflowActions.updateCostTypeSuccess({ costType }),
            ...reloadAfterCostTypeChange(claimId, recoveryId),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.updateCostTypeFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const updateApprovedAmounts$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(CostTypeApiService),
    toast = inject(DsToastService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.updateApprovedAmounts),
      mergeMap(({ claimId, recoveryId, costTypeId, request }) =>
        api.updateApprovedAmounts(costTypeId, request).pipe(
          tap(() =>
            toast.pushToast({
              id: 'approved-amounts-updated',
              tone: 'positive',
              duration: 3000,
              toastText: translate.instant(
                'vin.costTypes.cellRenderer.toasts.approvedAmountsUpdated'
              ),
            })
          ),
          mergeMap((costType: ICostTypeDetails) => [
            ClaimWorkflowActions.updateApprovedAmountsSuccess({ costType }),
            ...reloadAfterCostTypeChange(claimId, recoveryId),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.updateApprovedAmountsFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

interface CostTypeFileOperations {
  claimId: number;
  costTypeId: number;
  costType: CostTypeEnum;
  file: File | null;
  deleteFileId: string | null;
}

const runCostTypeFileOperations = (
  files: ClaimFilesService,
  toast: DsToastService,
  translate: TranslateService,
  operations: CostTypeFileOperations
): Observable<boolean> => {
  const deletion: Observable<boolean> =
    operations.deleteFileId !== null
      ? files
          .deleteFileFromClaim(operations.claimId, operations.deleteFileId)
          .pipe(
            map(() => true),
            catchError(() => of(false))
          )
      : of(true);

  if (operations.file === null) {
    return deletion;
  }

  const file = operations.file;
  return deletion.pipe(
    switchMap((deletionOk) =>
      files
        .uploadFileForClaim(
          operations.claimId,
          file,
          operations.costType,
          UploadType.UPLOAD,
          operations.costTypeId
        )
        .pipe(
          map(() => deletionOk),
          catchError((error) => {
            // Upload API errors are toasted by the error interceptor; S3 errors bypass it.
            if (error instanceof HttpErrorResponse) {
              return of(false);
            }
            toast.pushToast({
              id: 'file-upload-error',
              tone: 'critical',
              duration: 5000,
              toastText: translate.instant('vin.costTypes.fileUploadError'),
            });
            return of(false);
          })
        )
    )
  );
};

export const saveCostType$ = createEffect(
  (
    actions$ = inject(Actions),
    api = inject(CostTypeApiService),
    files = inject(ClaimFilesService),
    toast = inject(DsToastService),
    translate = inject(TranslateService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.saveCostType),
      mergeMap(
        ({ claimId, recoveryId, costTypeId, request, file, deleteFileId }) => {
          const persist =
            costTypeId === null
              ? api.createCostType(request)
              : api.updateCostType(costTypeId, request);
          return persist.pipe(
            switchMap((saved) =>
              runCostTypeFileOperations(files, toast, translate, {
                claimId,
                costTypeId: saved.id,
                costType: request.costType,
                file,
                deleteFileId,
              })
            ),
            tap((fileOperationsOk) => {
              if (fileOperationsOk) {
                toast.pushToast({
                  id: 'cost-type-saved',
                  tone: 'positive',
                  duration: 5000,
                  toastText: translate.instant('vin.costTypes.costTypeSaved'),
                });
              }
              void router.navigate(
                recoveryId === null
                  ? ['/claims', claimId, 'settlement']
                  : ['/claims', claimId, 'recovery']
              );
            }),
            map(() => ClaimWorkflowActions.saveCostTypeSuccess()),
            catchError((error) =>
              of(
                ClaimWorkflowActions.saveCostTypeFailure({
                  error: toApiError(error),
                })
              )
            )
          );
        }
      )
    ),
  { functional: true }
);

export const updateSettlementSummary$ = createEffect(
  (actions$ = inject(Actions), api = inject(SettlementSummaryApiService)) =>
    actions$.pipe(
      ofType(ClaimWorkflowActions.updateSettlementSummary),
      mergeMap(({ claimId, request }) =>
        api.updateSettlementSummary(claimId, request).pipe(
          mergeMap(() => [
            ClaimWorkflowActions.updateSettlementSummarySuccess(),
            ClaimWorkflowActions.loadSettlementSummary({ claimId }),
          ]),
          catchError((error) =>
            of(
              ClaimWorkflowActions.updateSettlementSummaryFailure({
                error: toApiError(error),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const claimWorkflowEffects = {
  initializeRecoveryTab$,
  initializeSettlementTab$,
  initializeUploadsTab$,
  uploadClaimFiles$,
  deleteClaimFiles$,
  loadClaim$,
  loadRecoveries$,
  loadRecoveryDamages$,
  loadSettlementSummary$,
  loadRecoverySummary$,
  loadCostTypeOptions$,
  addLiableToRecovery$,
  patchRecovery$,
  performClaimAction$,
  performRecoveryAction$,
  updateCostType$,
  updateApprovedAmounts$,
  saveCostType$,
  updateSettlementSummary$,
};
